import React, { useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowLeftIcon, ArrowUpRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getNextProject, getProjectBySlug } from '../data/projects';
import { DeepwatchHero } from './DeepwatchHero';
import { MediaCarousel } from './MediaCarousel';
import { SectionLabel } from './SectionLabel';
function ProjectFact({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="min-w-0" data-id="element-210" data-mp="1k6njz1eteebe">
      <dt className="font-display text-xs font-medium uppercase tracking-[0.1em] text-muted" data-id="element-211" data-mp="1k7gft7c8jjer">{label}</dt>
      <dd className="mt-1.5 break-words text-sm leading-snug text-foreground md:text-base" data-id="element-212" data-mp="1y5dgaett89yp">{value}</dd>
    </div>;
}
function StudySection({
  label,
  title,
  children
}: {
  label: string;
  title: string;
  children: React.ReactNode;
}) {
  const reduceMotion = useReducedMotion();
  return <motion.section initial={reduceMotion ? false : {
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-80px'
  }} transition={{
    duration: 0.28,
    ease: [0.22, 1, 0.36, 1]
  }} className="site-container my-20 max-w-6xl md:my-28" data-id="element-213" data-mp="3uzhwekg4jfkj">
      <div className="max-w-3xl" data-id="element-214" data-mp="12lzyj44y94q">
        <SectionLabel data-id="element-215" data-mp="11kmk7zndaju">{label}</SectionLabel>
        <h2 className="mt-5 text-3xl font-medium leading-[1.08] tracking-[-0.03em] md:text-5xl" data-id="element-216" data-mp="5u3yl0iorxmj">{title}</h2>
        <div className="mt-6 space-y-6 text-base leading-relaxed text-muted md:text-lg" data-id="element-217" data-mp="9vpaaijhi5v5">{children}</div>
      </div>
    </motion.section>;
}
function EvidenceList({
  items
}: {
  items: string[];
}) {
  return <ul className="mt-8 space-y-3 border-l border-border pl-5 text-base leading-relaxed text-muted md:text-lg" data-id="element-218" data-mp="jt3jpy6zpudy">
      {items.map(item => <li key={item} className="pl-1 marker:text-cobalt" data-id="element-219" data-mp="oqdjo7s09og1">{item}</li>)}
    </ul>;
}

/** Deepwatch narrative with reading layers and project evidence. */
export function DeepwatchCaseStudy() {
  const reduceMotion = useReducedMotion();
  const project = getProjectBySlug('deepwatch');
  const next = getNextProject('deepwatch');
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'instant' as ScrollBehavior
    });
  }, []);
  if (!project) return null;
  return <article className="pb-24 pt-28 md:pb-32 md:pt-36" data-id="element-220" data-mp="2siw2t6w46igk">
      <div className="site-container" data-id="element-221" data-mp="2nihvxs691ek4">
        <Link to="/#work" className="group inline-flex items-center gap-2 font-display text-base font-medium uppercase tracking-[0.1em] text-muted transition-colors duration-200 hover:text-cobalt focus-visible:outline-none focus-visible:text-cobalt" data-id="element-222" data-mp="2ngn6q1ny30a8">
          <ArrowLeftIcon size={16} aria-hidden="true" className="transition-transform duration-200 ease-out group-hover:-translate-x-0.5" data-id="element-223" data-mp="3b6r965pectzn" />
          Back to work
        </Link>
      </div>

      <header className="site-container pt-12 md:pt-16" data-id="element-224" data-mp="3k7ph2z2yhlni">
        <motion.div initial={reduceMotion ? false : {
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.28,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-225" data-mp="3k64lr78thyol">
          <SectionLabel data-id="element-226" data-mp="2r39uuz9fxbz">Enterprise product design</SectionLabel>
        </motion.div>
        <motion.h1 initial={reduceMotion ? false : {
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.28,
        delay: reduceMotion ? 0 : 0.04,
        ease: [0.22, 1, 0.36, 1]
      }} className="mt-7 max-w-4xl text-balance text-4xl font-medium leading-[1.04] tracking-[-0.04em] md:text-6xl lg:text-7xl" data-id="element-227" data-mp="7ntqpkiw2wyg">
          Deepwatch Security Center
        </motion.h1>
      </header>

      <motion.div initial={reduceMotion ? false : {
      opacity: 0,
      y: 18
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.28,
      delay: reduceMotion ? 0 : 0.08,
      ease: [0.22, 1, 0.36, 1]
    }} className="site-container mt-8 md:mt-10" data-id="element-228" data-mp="3kwpw3u71ytpd">
        <div className="relative aspect-[16/10] w-full overflow-hidden bg-border md:aspect-[2/1]" data-id="element-229" data-mp="2x757ovs4r48p">
          <DeepwatchHero variant="case-study" data-id="element-230" data-mp="323lyjxqprwph" />
        </div>
      </motion.div>

      <motion.dl initial={reduceMotion ? false : {
      opacity: 0,
      y: 14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.28,
      delay: reduceMotion ? 0 : 0.12,
      ease: [0.22, 1, 0.36, 1]
    }} className="site-container mt-8 grid grid-cols-2 gap-x-6 gap-y-7 md:mt-10 md:grid-cols-4 md:gap-x-10" data-id="element-231" data-mp="8d75hamo798e">
        <ProjectFact label="Client" value={project.client} data-id="element-232" data-mp="3qqogceap3q73" />
        <ProjectFact label="Role" value={project.role} data-id="element-233" data-mp="3ls3nizxrw8so" />
        <ProjectFact label="Year" value="2021 – 2024" data-id="element-234" data-mp="3lph86e4n4ozw" />
        <ProjectFact label="Team" value="2 designers" data-id="element-235" data-mp="3grz719wv7e2g" />
      </motion.dl>

      <StudySection label="Overview" title="Making security work visible" data-id="element-236" data-mp="3cqnf2xqiqgr3">
        <p className="font-medium text-foreground md:text-xl" data-id="element-237" data-mp="37s2e2w3fzt1l">A clearer operational picture for the people running security—and the customers depending on it.</p>
        <p data-id="element-238" data-mp="1azadahjukoz9">Deepwatch is a managed security service provider offering human-driven Managed Detection and Response (MDR). It helps enterprise security teams stay ahead of threats, oversee their security posture, and monitor systems 24/7/365.</p>
        <p data-id="element-239" data-mp="1ij1pxrdfo25q">I joined as a Senior Product Designer alongside one other designer, initially focused on building the visual language for the emerging Security Center.</p>
      </StudySection>

      <StudySection label="The problem" title="Reporting created work—and doubt" data-id="element-240" data-mp="1nce4f8oeeskr">
        <p className="font-medium text-foreground md:text-xl" data-id="element-241" data-mp="1sb7fkug7q7hr">Customer Success Managers were rebuilding the security story by hand, while customers had little visibility into the service they were paying for.</p>
        <p data-id="element-242" data-mp="zma9dppa015d">Each customer report meant hours curating a PowerPoint presentation: showing work completed, surfacing critical events, and recreating the current picture after the fact. Meanwhile, customers could not see “how the sausage is made,” which made it harder to trust the data or feel confident they were getting value.</p>
        <EvidenceList items={['Hours of recurring report preparation for Customer Success Managers', 'Lost or duplicated work between Deepwatch teams and client conversations', 'Limited accountability for who was handling a specific ticket or task', 'Low visibility into security posture, endpoint health, and the work in progress']} data-id="element-243" data-mp="2jzrcfng2sbum" />
      </StudySection>

      <StudySection label="Solution" title="One security center, shared context" data-id="element-244" data-mp="oww4qmweoubx">
        <p className="font-medium text-foreground md:text-xl" data-id="element-245" data-mp="17a21pgtrwz6">The Security Center became a shared source of truth for client security posture, operational work, and strategic decision-making.</p>
        <p data-id="element-246" data-mp="ax35g36td5sc">The business needed a dashboard where both clients and Deepwatch professionals could monitor security posture, view open, closed, and in-progress tickets, and take action in the same context.</p>
        <EvidenceList items={['Security posture, ticket status, and work-in-progress in one view', 'CISO and senior-leadership visibility into ongoing projects and vulnerabilities across the MITRE board', 'Endpoint Detection and Response health, including blocked or frozen devices and tracking-software updates', 'Telemetry visibility that distinguishes routine information from critical issues', 'A robust ticket library and filtering system to help users find the detail they need']} data-id="element-247" data-mp="3ely258gy711e" />
      </StudySection>

      <section className="site-container my-20 max-w-6xl md:my-28" aria-label="Deepwatch product evolution" data-id="element-248" data-mp="1l1cw4wj36jsv">
        <MediaCarousel label="Product evolution" items={[{
        src: 'https://cdn.magicpatterns.com/uploads/iKWNYh5zd5CwmfFM2qTDME/dw-home-v1.png',
        alt: 'Early Deepwatch Security Center home dashboard',
        caption: 'Home v1 — establishing security posture, reporting, and ticket visibility'
      }, {
        src: 'https://cdn.magicpatterns.com/uploads/i7AhifSMY4yPCfsA5xz9fA/dw-global-datefilter.png',
        alt: 'Deepwatch global date filter across the home dashboard',
        caption: 'Global date filter — applying a consistent time context across reporting'
      }, {
        src: 'https://cdn.magicpatterns.com/uploads/dUjzTHw74ScMjWBC228Nmo/dw-home-v2.png',
        alt: 'Refined Deepwatch Security Center home dashboard',
        caption: 'Home v2 — refining the shared operational overview'
      }]} data-id="element-249" data-mp="2cuwsvq94y1rt" />
      </section>

      <StudySection label="Design system" title="A system that expanded with the product" data-id="element-250" data-mp="156fb46iogucn">
        <p className="font-medium text-foreground md:text-xl" data-id="element-251" data-mp="22te0dw71j00i">The design system began with form elements and messaging, then scaled to support the operational complexity of Security Center.</p>
        <p data-id="element-252" data-mp="1t2t6gm2bajnx">As features grew, the component library grew with them: chat bubbles, notifications, date pickers, and data visualizations gave product teams a consistent foundation for increasingly complex workflows.</p>
      </StudySection>

      <section className="site-container my-20 max-w-6xl md:my-28" aria-label="Deepwatch design system examples" data-id="element-253" data-mp="2ll6rfl2jub5l">
        <MediaCarousel label="Design system examples" items={[{
        src: 'https://cdn.magicpatterns.com/uploads/5NtYA6VDjuKpKMJoctKbbx/ds-atoms.png',
        alt: 'Deepwatch design-system form elements and foundational controls',
        caption: 'Foundations — form elements and control states'
      }, {
        src: 'https://cdn.magicpatterns.com/uploads/kVMmyLYyE3GpQyxojhAc6G/ds-organisms.png',
        alt: 'Deepwatch design-system organisms including messages, notifications, and date pickers',
        caption: 'Scaled patterns — messaging, notifications, and date pickers'
      }, {
        src: 'https://cdn.magicpatterns.com/uploads/tfFe9QhPwLKdDfJsgdUuPF/ds-dataviz.png',
        alt: 'Deepwatch data visualization components and dashboard chart patterns',
        caption: 'Data visualization — dashboard and chart patterns'
      }]} data-id="element-254" data-mp="2gqsrr6guthv4" />
      </section>

      <StudySection label="Research" title="From visual language to product direction" data-id="element-255" data-mp="3qry9yq8mpdz6">
        <p className="font-medium text-foreground md:text-xl" data-id="element-256" data-mp="3vp6u2wdc7s32">As my cybersecurity knowledge deepened, my work expanded from design language into the feature and roadmap decisions behind it.</p>
        <p data-id="element-257" data-mp="34o9y553oe31j">I took on larger product areas including Ticketing, Notifications, Environmental Health, and Endpoint Detection and Response. I worked with leadership, product, and engineering to shape the roadmap through card-sorting workshops and user interviews.</p>
        <EvidenceList items={['Interviewed internal Deepwatch teams and customer stakeholders', 'Clustered feedback into shared needs, opportunities, and unresolved questions', 'Used synthesis to ground Environmental Health and data-visibility decisions in user evidence']} data-id="element-258" data-mp="3egfo69adaae3" />
      </StudySection>

      <section className="site-container my-20 max-w-6xl md:my-28" aria-label="Deepwatch research and synthesis" data-id="element-259" data-mp="18b7cqhdvb3y">
        <MediaCarousel label="Research and synthesis" items={[{
        src: 'https://cdn.magicpatterns.com/uploads/a2hSvUEVihWWgjtphC9uQd/dw-research.png',
        alt: 'Deepwatch research board with interview notes from customers and internal teams',
        caption: 'Interview capture — customer and internal feedback'
      }, {
        src: 'https://cdn.magicpatterns.com/uploads/25MPN6ixEdBHYmr4EGkrTY/dw-research-clustering.png',
        alt: 'Deepwatch research board clustering feedback into environment health and data visualization themes',
        caption: 'Clustering — organizing themes and opportunity areas'
      }, {
        src: 'https://cdn.magicpatterns.com/uploads/vwd59pKMyRawcycp46vv8p/dw-research-synthesis.png',
        alt: 'Deepwatch Environment Health research synthesis with interview insights and open questions',
        caption: 'Synthesis — Environment Health findings and open questions'
      }]} data-id="element-260" data-mp="1y1edyw2by2a6" />
      </section>

      <section className="site-container my-20 max-w-6xl md:my-28" aria-label="Deepwatch feature areas" data-id="element-261" data-mp="20h2p7uo4pcyu">
        <MediaCarousel label="Feature areas" items={[{
        src: 'https://cdn.magicpatterns.com/uploads/cg4sMSxa3c6yfHzwM1RZjT/dw-dc.png',
        alt: 'Deepwatch Detection Coverage dashboard with MITRE coverage and endpoint data',
        caption: 'Detection Coverage — translating defensive posture into an actionable overview'
      }, {
        src: 'https://cdn.magicpatterns.com/uploads/jgtquufcQR5DTqSB6bMUuh/dw-edr-v2.png',
        alt: 'Deepwatch Endpoint Detection and Response dashboard with device and license health',
        caption: 'Endpoint Detection and Response — surfacing device health and utilization'
      }]} data-id="element-262" data-mp="sz3fqhk9e0rt" />
      </section>

      <StudySection label="Outcome" title="More trust, less reporting overhead" data-id="element-263" data-mp="ci0lc9jfuuuw">
        <p className="font-medium text-foreground md:text-xl" data-id="element-264" data-mp="3tyi82wwx92ea">Security Center made the work visible: a shared place for clients and Deepwatch professionals to align during meetings and in ongoing conversations.</p>
        <EvidenceList items={['Reduced time spent preparing recurring reports, giving Deepwatch professionals more room for high-value work', 'Strengthened client trust with visibility into security posture and work performed on their behalf', 'Created a durable product and design-system foundation for ongoing Security Center expansion', 'Contributed design work that helped support a Series B+ funding round']} data-id="element-265" data-mp="3kybxbislygit" />
      </StudySection>

      <div className="site-container mt-24 md:mt-32" data-id="element-266" data-mp="3aavsco180591">
        <SectionLabel data-id="element-267" data-mp="3ae9ubv5z2cfy">Next case study</SectionLabel>
        <Link to={`/work/${next.slug}`} className="group mt-10 flex items-end justify-between gap-6 border-b border-border pb-8 focus-visible:outline-none" data-id="element-268" data-mp="3p7wlmvz2889m">
          <div className="min-w-0" data-id="element-269" data-mp="1w3vv35vuqlu">
            <h2 className="break-words text-4xl font-medium leading-[1.03] tracking-[-0.035em] transition-colors duration-200 group-hover:text-cobalt md:text-6xl" data-id="element-270" data-mp="1v2bg82z8rrg">{next.title}</h2>
            <p className="mt-3 text-muted" data-id="element-271" data-mp="btrrrwf8645l">{next.client}</p>
          </div>
          <ArrowUpRightIcon size={34} aria-hidden="true" className="mb-1 shrink-0 transition-transform duration-200 ease-out group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-cobalt" data-id="element-272" data-mp="6v88y4vxh8hg" />
        </Link>
      </div>
    </article>;
}